import { Component, OnInit, ViewChild, Optional, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { Router } from '@angular/router';
import { RosterService } from '../../../services/roster/roster.service';
import { SnackbarService } from '../../../common/services/snackbar.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RosterCloneDialogComponent } from '../roster-clone-dialog/roster-clone-dialog.component';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { RequestData, SearchSort } from 'src/app/common/models/request-data.model';

@Component({
  selector: 'app-roster-list',
  templateUrl: './roster-list.component.html',
  styleUrl: './roster-list.component.scss',
  standalone: false
})
export class RosterListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>([]);
  isLoading = true;
  isInitializing = true;

  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };
  filter: FilterListItemModel[] = [];
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];

  weeksList: any[] = [];
  yearsList: any[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  constructor(
    private rosterService: RosterService,
    private router: Router,
    private snackBarService: SnackbarService,
    private dialog: MatDialog,
    private stateManagerService: StateManagerService,
    @Optional() public dialogRef: MatDialogRef<RosterListComponent>
  ) { }

  ngOnInit(): void {
    if (this.dialogRef) {
      this.displayedColumns = ['item', 'startDate', 'endDate', 'weekNumber', 'year'];
    } else {
      this.displayedColumns = ['item', 'startDate', 'endDate', 'weekNumber', 'year', 'action'];
    }
    this.initializeFilters();
    this.loadWeekDefs();
  }

  ngAfterViewInit(): void {
    // Left empty since we trigger loadPreviousState dynamically once loadWeekDefs resolves
  }

  initializeFilters(): void {
    const filters: FilterListItemModel[] = [
      {
        title: "Week",
        column: "employee_dr_hdr.week_number",
        type: "list",
        excludeFreeSearch: true,
        values: this.weeksList
      },
      {
        title: "Year",
        column: "employee_dr_hdr.year",
        type: "list",
        includeFreeSearch: true,
        values: this.yearsList
      }
    ];

    this.filter = filters;
  }

  loadWeekDefs(): void {
    this.rosterService.getWeekDefs().subscribe({
      next: (res: any) => {
        if (res && res.status === 'SUCCESS') {
          const weekDefs = res.data || [];

          // Unique week numbers
          const weeksSet = new Set<number>();
          weekDefs.forEach((item: any) => {
            if (item.week_no != null) {
              weeksSet.add(Number(item.week_no));
            }
          });
          const weeksArray = Array.from(weeksSet).sort((a, b) => a - b);
          this.weeksList = weeksArray.map(w => ({
            title: String(w),
            value: String(w)
          }));

          // Unique years
          const yearsSet = new Set<number>();
          weekDefs.forEach((item: any) => {
            if (item.year != null) {
              yearsSet.add(Number(item.year));
            }
          });
          const yearsArray = Array.from(yearsSet).sort((a, b) => b - a);
          this.yearsList = yearsArray.map(y => ({
            title: String(y),
            value: String(y)
          }));

          this.initializeFilters();
        }

        this.loadPreviousState();
        this.isInitializing = false;
      },
      error: (err) => {
        console.error('Failed to load week definitions', err);
        this.initializeFilters();
        this.loadPreviousState();
        this.isInitializing = false;
      }
    });
  }

  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);
    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }
      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      }
      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, true);
    } else {
      this.setSortColumn('employee_dr_hdr.start_date', 'desc');
      this.loadRosters();
    }
  }

  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    if (this.sort) {
      let activeId = column;
      if (column.startsWith('employee_dr_hdr.')) {
        activeId = column.replace('employee_dr_hdr.', '');
      }
      if (activeId === 'start_date') activeId = 'startDate';
      if (activeId === 'end_date') activeId = 'endDate';
      if (activeId === 'week_number') activeId = 'weekNumber';

      this.sort.sort({
        id: activeId,
        start: direction,
        disableClear: false,
      });
    }
  }

  buildRequestData(): RequestData {
    let searchParam = this.searchColumFilter;
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch || [],
          advSearch: searchParam.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.columnSortData,
      }
    };
    return requestData;
  }

  loadRosters() {
    this.isLoading = true;
    let requestData = this.buildRequestData();
    this.rosterService.getRosterList(requestData).subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        if (res.data && res.data.rosters) {
          this.dataSource.data = res.data.rosters;
          this.pagingData.length = res.data.total_count || 0;
        } else {
          this.dataSource.data = res.data || [];
          this.pagingData.length = (res.data || []).length;
        }

        if (this.pagingData.length > 0 && !this.dialogRef) {
          this.stateManagerService.setState(this.constructor.name, requestData);
        }
      }
      this.isLoading = false;
    });
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadRosters();
  }

  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadRosters();
  }

  sortData($event: any): void {
    if (this.sort) {
      let sortColumn = this.sort.active;
      if (sortColumn === 'startDate') sortColumn = 'start_date';
      if (sortColumn === 'endDate') sortColumn = 'end_date';
      if (sortColumn === 'weekNumber') sortColumn = 'week_number';

      this.columnSortData = [{ column: 'employee_dr_hdr.' + sortColumn, order: this.sort.direction || 'asc' }];
      if (!this.isInitializing) {
        this.loadRosters();
      }
    }
  }

  onEdit(row: any) {
    if (this.dialogRef) {
      this.dialogRef.close({ week_number: row.week_number, year: row.year });
    } else {
      this.router.navigate(['/roster/planner'], { queryParams: { week_number: row.week_number, year: row.year } });
    }
  }

  onDelete(row: any) {
    const dialogData = new ConfirmDialogModel('Confirm Delete', 'Are you sure you want to delete this entire roster?');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.rosterService.deleteRoster(row.id).subscribe((res: any) => {
          if (res.status === 'SUCCESS') {
            this.snackBarService.open('Roster deleted successfully', 'success');
            this.loadRosters();
          } else {
            this.snackBarService.open(res.message, 'error');
          }
        });
      }
    });
  }

  onConfirm(row: any) {
    const dialogData = new ConfirmDialogModel('Confirm Roster', 'Are you sure you want to confirm this roster? This will mark it as Approved.');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.rosterService.confirmRoster(row.id, 1).subscribe((res: any) => {
          if (res.status === 'SUCCESS') {
            this.snackBarService.open('Roster confirmed successfully', 'success');
            this.loadRosters();
          } else {
            this.snackBarService.open(res.message, 'error');
          }
        });
      }
    });
  }

  onClone(row: any) {
    const dialogRef = this.dialog.open(RosterCloneDialogComponent, {
      width: '450px',
      data: { source_week: row.week_number, source_year: row.year }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const cloneParams = {
          source_week: row.week_number,
          source_year: row.year,
          target_week: result.target_week,
          target_year: result.target_year,
          clone_type: result.clone_type
        };

        this.rosterService.cloneRoster({ ...cloneParams, check_only: true }).subscribe((res: any) => {
          if (res.status === 'EXISTS') {
            const dialogData = new ConfirmDialogModel(
              'Overwrite Roster',
              res.message || `Roster for target week already exists. Do you want to overwrite it?`
            );
            const confirmRef = this.dialog.open(ConfirmDialogComponent, {
              width: '400px',
              data: dialogData
            });

            confirmRef.afterClosed().subscribe(confirmResult => {
              if (confirmResult) {
                this.executeListClone(cloneParams);
              }
            });
          } else {
            this.executeListClone(cloneParams);
          }
        });
      }
    });
  }

  private executeListClone(cloneParams: any) {
    this.isLoading = true;
    this.rosterService.cloneRoster(cloneParams).subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.snackBarService.open(res.message || 'Roster successfully cloned', 'success');
        this.loadRosters();
      } else {
        this.snackBarService.open(res.message, 'error');
        this.isLoading = false;
      }
    });
  }

  addClicked() {
    this.router.navigate(['/roster/planner']);
    if (this.dialogRef) this.dialogRef.close();
  }

}
